/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { useEffect } from "react";

import { useUnityDispatch } from "../../viewModel";
import {
  isDevToolsBuild,
  openFeatureOverride
} from "../../viewModel/featureOverride";
import { createSecretCodeInterceptor } from "../../viewModel/navigation";
import type { NavigationEngine } from "../../viewModel/navigation";

/** The digit sequence that opens the debug-only Feature Override screen. */
const OVERRIDE_CODE: string = "123456";

/** Name used to register/remove the interceptor on the engine. */
const INTERCEPTOR_NAME: string = "featureOverrideCode";

/**
 * Registers the hidden `123456` trigger for the developer/QE Feature Override
 * screen, mirroring {@link useAppVersionOverrideCode}.
 *
 * Debug builds only: `isDevToolsBuild()` is a constant `false` in release, so
 * the interceptor is never installed, `123456` is never watched for, and the
 * screen it would open stays unreachable.
 *
 * @param engine - The NavigationEngine instance from context.
 */
export function useFeatureOverrideCode(engine: NavigationEngine): void {
  const dispatch = useUnityDispatch();

  useEffect((): (() => void) | undefined => {
    if (!isDevToolsBuild()) {
      return undefined;
    }

    const interceptor = createSecretCodeInterceptor(OVERRIDE_CODE, (): void => {
      dispatch(openFeatureOverride());
    });

    engine.getInterceptors().use(INTERCEPTOR_NAME, interceptor);

    return (): void => {
      engine.getInterceptors().remove(INTERCEPTOR_NAME);
    };
  }, [engine, dispatch]);
}
